import React, { useEffect, useState } from 'react'
import { Card } from '@radix-ui/themes'
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react'
import { QuestionType } from '@/types/question'
// import { truncate } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import Hstack from '../hstack'
import type { Question } from './question'

interface QuestionCardProps {
  question: Question
  questionNumber: number
  isActive?: boolean
  isDimmed?: boolean
  activeId?: string | null
  setActiveId?: (id: string | null) => void
  isNested?: boolean
  onAcceptAI?: () => void
  onRegrade?: () => void
  onMarkChange?: (marks: number) => void
  onFeedbackChange?: (feedback: string) => void
  onSave?: () => void
  onCancel?: () => void
}

export const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  questionNumber,
  isActive,
  isDimmed,
  activeId,
  setActiveId,
  isNested,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const toggleExpanded = () => {
    setIsExpanded((prev) => !prev)
  }

  const isChoice =
    question.type === QuestionType.multiple_choice ||
    question.type === QuestionType.single_choice
  const isCaseStudy = question.type === QuestionType.CaseStudy

  // Derive display type from sub-questions when present
  const displayType = (() => {
    const getTypeDisplayName = (type: string) => {
      switch (type.toLowerCase()) {
        case QuestionType.text:
          return 'Standard'
        case QuestionType.single_choice:
          return 'Single Choice'
        case QuestionType.multiple_choice:
          return 'Multiple Choice'
        case QuestionType.CaseStudy:
          return 'Case Study'
        default:
          return type.toUpperCase()
      }
    }

    if (question.subQuestions && question.subQuestions.length > 0) {
      const firstType = question.subQuestions[0].type
      const allSame = question.subQuestions.every((sq) => sq.type === firstType)
      if (allSame) return getTypeDisplayName(firstType)
    }
    return getTypeDisplayName(question.type)
  })()

  useEffect(() => {
    if (isActive) setIsExpanded(true)
  }, [isActive])

  // If case study with single sub-question, render sub-question directly -- but only
  // when the parent adds nothing. The parent holds the scenario and the sub-question the
  // task, and collapsing them dropped the scenario; they hold the same text when the
  // matrix file follows the template, and then the parent is safe to skip.
  if (
    isCaseStudy &&
    question.subQuestions &&
    question.subQuestions.length === 1 &&
    (!question.question?.trim() ||
      question.question.trim() ===
        (question.subQuestions[0]?.question ?? '').trim())
  ) {
    return (
      <QuestionCard
        question={question.subQuestions[0] as unknown as Question}
        questionNumber={questionNumber}
        isActive={activeId === question.subQuestions?.[0]?.id}
        isDimmed={!!activeId && activeId !== question.subQuestions?.[0]?.id}
        activeId={activeId}
        setActiveId={setActiveId}
        isNested={isNested}
        onRegrade={() => {
          if (!setActiveId) return
          if (activeId) return
          setActiveId(question.subQuestions?.[0]?.id || null)
        }}
        onCancel={() => setActiveId && setActiveId(null)}
        onSave={() => setActiveId && setActiveId(null)}
      />
    )
  }

  return (
    <Card
      className={`mb-4 cursor-pointer rounded-md border transition-all duration-200 ${
        isExpanded
          ? `${isActive ? 'border-[#F6F7F9]' : 'border-blue-300'} bg-white`
          : 'border-gray-200 bg-[#F6F7F9] hover:border-blue-300 hover:bg-white'
      } ${isDimmed ? 'pointer-events-none opacity-40 blur-[1px]' : ''} ${
        isActive
          ? 'border-[#0069E0] [box-shadow:0px_5px_5px_-2.5px_#023A7B0A,0px_32px_64px_-12px_#023A7B24]'
          : ''
      }`}
      onClick={(e) => {
        if (isNested) e.stopPropagation()
        if (isDimmed) return
        if (activeId && !isActive) return
        toggleExpanded()
      }}
    >
      <div className='p-6'>
        {/* Header */}
        <div className='mb-4 flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <span className='text-sm text-gray-600'>
              Question {questionNumber}
            </span>
          </div>
          <Hstack justify={'between'}>
            <Hstack align={'center'} className='space-x-2'>
              {question.type !== QuestionType.CaseStudy && (
                <>
                  {question.point && (
                    <Badge
                      variant='secondary'
                      className='border-gray-300 bg-white'
                    >
                      {question.point === 1
                        ? '1 point'
                        : `${question.point} mark`}
                    </Badge>
                  )}
                </>
              )}

              <Badge variant='secondary' className='border-gray-300 bg-white'>
                {displayType}
              </Badge>
            </Hstack>
            {question.type !== QuestionType.text && (
              <>
                {isExpanded ? (
                  <IconChevronUp className='ml-2 size-4 text-gray-600' />
                ) : (
                  <IconChevronDown className='ml-2 size-4 text-gray-600' />
                )}
              </>
            )}
          </Hstack>
        </div>

        <div className='mb-6'>
          <p className='leading-relaxed font-medium break-words whitespace-pre-wrap text-gray-900'>
            {/* {truncate(question.question, 500)} */}
            {question.question}
          </p>
        </div>

        {isExpanded && isChoice && question.options && (
          <div className='mb-6 rounded-md bg-[#F6F7F9] p-4'>
            <h4 className='mb-3 text-sm font-medium text-gray-700'>Answer</h4>
            <div className='space-y-2'>
              {question.options.map((option, index) => {
                return (
                  <div key={index} className='flex items-center p-3'>
                    <div
                      className={`mr-3 h-4 min-h-4 w-4 min-w-4 rounded-sm border-2 border-gray-300`}
                    ></div>
                    <span className='text-gray-800'>{option}</span>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Show sub-questions for case studies with multiple sub-questions */}
        {isExpanded &&
          isCaseStudy &&
          question.subQuestions &&
          question.subQuestions.length > 1 && (
            <div
              onClick={(e) => {
                e.stopPropagation()
              }}
            >
              {question.subQuestions.map((sub, idx) => (
                <div
                  key={sub.id}
                  className={
                    activeId
                      ? activeId === sub.id
                        ? ''
                        : 'pointer-events-none opacity-40 blur-[1px]'
                      : ''
                  }
                >
                  <QuestionCard
                    question={sub as unknown as Question}
                    questionNumber={idx + 1}
                    isActive={activeId === sub.id}
                    isDimmed={!!activeId && activeId !== sub.id}
                    activeId={activeId}
                    setActiveId={setActiveId}
                    isNested
                    onRegrade={() => {
                      if (!setActiveId) return
                      if (activeId) return
                      setActiveId(sub.id)
                    }}
                    onCancel={() => setActiveId && setActiveId(null)}
                    onSave={() => setActiveId && setActiveId(null)}
                  />
                </div>
              ))}
            </div>
          )}
      </div>
    </Card>
  )
}
